import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { typography, spacing } from '../../tokens';
import { Colors } from '../../../../themes/colors';

export type TagStatus = 'join' | 'replies' | 'set';

export interface TagProps {
  /**
   * The tag status that determines the color scheme
   */
  status: TagStatus;

  /**
   * The text content to display
   */
  text: string;

  /**
   * Test ID for testing
   */
  testID?: string;
}

// The two semantic tags keep a fixed hue regardless of theme.
const statusStyles: Record<'join' | 'replies', { backgroundColor: string; textColor: string }> = {
  join: {
    backgroundColor: '#D0E5F9',
    textColor: '#4A90E2',
  },
  replies: {
    backgroundColor: '#D3F4E7',
    textColor: '#24835D',
  },
};

export const Tag: React.FC<TagProps> = ({
  status,
  text,
  testID,
}) => {
  // `set` is the one non-semantic tag: a plain neutral chip that flips with
  // the theme. Read through the `Colors.role` Proxy here, in the render body,
  // rather than in the module-scope `statusStyles` map above — on native the
  // Proxy resolves at read time, so a value captured at import time would
  // freeze at whichever mode was active then and never update on a flip.
  const { backgroundColor, textColor } =
    status === 'set'
      ? {
          backgroundColor: Colors.role.surfaceInverse,
          textColor: Colors.role.textOnInverse,
        }
      : statusStyles[status];

  return (
    <View
      style={[styles.container, { backgroundColor }]}
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={`${status}: ${text}`}
    >
      <Text style={[styles.text, { color: textColor }]}>{text}</Text>
    </View>
  );
};

Tag.displayName = 'Tag';

const styles = StyleSheet.create({
  container: {
    height: 24,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 99, // Full pill shape
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  text: {
    ...typography.label.medium,
  },
});
